import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { approvePhoto, photosModerationQuery, recipePhotosQuery, rejectPhoto } from '@/api'
import { PhotoModerationEntry, UserRecipePhoto } from '@/api/types'

type UsePhotosModerationOptions = {
  recipeId?: number
}

export default function usePhotosModeration(options: UsePhotosModerationOptions = {}) {
  const PHOTOS_QUERY_KEY = photosModerationQuery().queryKey
  const queryClient = useQueryClient()

  const updateListCache = (id: number) => {
    queryClient.setQueryData<PhotoModerationEntry[]>(PHOTOS_QUERY_KEY, (oldData) => {
      if (!oldData) return
      return oldData.filter((photo) => photo.id !== id)
    })
  }

  const approvePhotoMutation = useMutation({
    mutationFn: (idRecipePhoto: number) => approvePhoto(idRecipePhoto),
    onMutate: updateListCache,
    onSuccess: (_, idRecipePhoto) => {
      if (options.recipeId) {
        queryClient.setQueryData(
          recipePhotosQuery(options.recipeId).queryKey,
          (oldData: UserRecipePhoto[]) => {
            if (!oldData) return

            return oldData.map((photo) =>
              photo.idRecipePhoto === idRecipePhoto
                ? { ...photo, latestModerationStatus: 'APPROVED' }
                : photo,
            )
          },
        )
        queryClient.invalidateQueries({ queryKey: ['recipeThumbnail', options.recipeId] })
      }
    },
    onError: () => {
      toast.error('Failed to approve photo')
    },
  })

  const rejectPhotoMutation = useMutation({
    mutationFn: (idRecipePhoto: number) => rejectPhoto(idRecipePhoto),
    onMutate: updateListCache,
    onSuccess: (_, idRecipePhoto) => {
      if (options.recipeId) {
        queryClient.setQueryData(
          recipePhotosQuery(options.recipeId).queryKey,
          (oldData: UserRecipePhoto[]) => {
            if (!oldData) return

            return oldData.filter((photo) => photo.idRecipePhoto !== idRecipePhoto)
          },
        )
        queryClient.invalidateQueries({ queryKey: ['recipeThumbnail', options.recipeId] })
      }
    },
    onError: () => {
      toast.error('Failed to reject photo')
    },
  })

  return {
    approvePhoto: approvePhotoMutation.mutateAsync,
    rejectPhoto: rejectPhotoMutation.mutateAsync,
  }
}
